import { createStore } from 'vuex';
import VuexPersistence from 'vuex-persist';
import axios from 'axios';
import qs from 'qs';
import { API_ROOT, logApiError } from '@/store/api';
import { PRODUCTION, VUEX_KEY, DEBUG } from './constants';
import { MODEL_FORM_VERSIONS } from '@/store/model-schema';

import account from '@/store/account';
import admin from '@/store/admin';
import { FLAG_ADVISOR_SELF_REGISTRATION, isFeatureEnabled } from '@/store/feature_flags';
import ai from '@/store/ai';
import configuration from '@/store/configuration';
import content from '@/store/content';
import error from '@/store/error';
import guided_planning from '@/store/guided_planning';
import hamilton from '@/store/hamilton';
import onboarding from '@/store/onboarding';
import marketing from '@/store/marketing';
import media from '@/store/media';
import model from '@/store/model';
import notifications from '@/store/notifications';
import notifier from '@/store/notifier';
import questionnaire from './questionnaire';
import reports from '@/store/reports';
import gift_tax from '@/store/gift_tax';
import scenarios from '@/store/scenarios';
import subscriptions from '@/store/subscriptions';
import tasks from '@/store/tasks';
import tax_season from '@/store/tax_season';
import transition_animation from '@/store/transition_animation';
import ui from '@/store/ui';
import uploads from '@/store/uploads';

import { logError } from '@/constants';
import { persistedDistinctId } from '@/plugins/posthog';
import {
    identifyUser,
    trackUserLoggedIn,
    trackAccountRegistered,
    trackAddedClient,
    trackAccountActivated,
} from '@/posthog_tracking';

// Clears persisted calculator forms whose stored schema version no longer matches
// the current MODEL_FORM_VERSIONS — so users with obsolete form shapes start fresh
// instead of submitting data the backend no longer accepts.
function restoreAndPruneStaleForms(key, storage) {
    const raw = (storage || window.localStorage).getItem(key);
    if (!raw) return {};
    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return {};
    }
    if (!parsed.model) return parsed;
    Object.keys(MODEL_FORM_VERSIONS).forEach(formKey => {
        const entry = parsed.model[formKey];
        if (!entry || entry.version !== MODEL_FORM_VERSIONS[formKey]) {
            delete parsed.model[formKey];
        }
    });
    return parsed;
}

let persistenceStorage = window.localStorage;
if (__DEV_TOOLS__) {
    const { pageStateStorage } = await import('../dev/page_state_config');
    persistenceStorage = pageStateStorage(window.localStorage, window.sessionStorage);
}
const vuexLocalStorage = new VuexPersistence({
    key: VUEX_KEY,
    storage: persistenceStorage,
    restoreState: restoreAndPruneStaleForms,
    reducer: state => ({
        // NOTE: auth (tokens) + impersonatedUser (PII) are intentionally NOT persisted.
        // Auth lives in HttpOnly cookies; the user is rehydrated via refreshJWT on load.
        extraHeaders: state.extraHeaders,
        admin: {
            headers: state.admin.headers,
        },
        configuration: {
            availableProducts: state.configuration.availableProducts,
            productsVersion: state.configuration.productsVersion,
        },
        guided_planning: {
            guidedPlanningAnswers: state.guided_planning.guidedPlanningAnswers,
            guidedPlanningGraph: state.guided_planning.guidedPlanningGraph,
            guidedPlanningInfo: state.guided_planning.guidedPlanningInfo,
            guidedPlanningProgress: state.guided_planning.guidedPlanningProgress,
        },
        marketing: {
            advisorProfile: state.marketing.advisorProfile,
            advisoryPartnerProfile: state.marketing.advisoryPartnerProfile,
            brandProfile: state.marketing.brandProfile,
            marketingEvents: state.marketing.marketingEvents,
        },
        ui: {
            viewAs: state.ui.viewAs,
        },
        model: {
            crutModelForm: state.model.crutModelForm,
            crutReportData: state.model.crutReportData,
            gratModelForm: state.model.gratModelForm,
            iraModelForm: state.model.iraModelForm,
            crutModelLength: state.model.crutModelLength,
            crutModelStrategy: state.model.crutModelStrategy,
            oilGasModelForm: state.model.oilGasModelForm,
            ppliModelForm: state.model.ppliModelForm,
            qsbsModelForm: state.model.qsbsModelForm,
            solarModelForm: state.model.solarModelForm,
            totalOrdinaryIncome: state.model.totalOrdinaryIncome,
            totalLtcgIncome: state.model.totalLtcgIncome,
        },
        onboarding: {
            onboardingInfo: state.onboarding.onboardingInfo,
            onboardings: state.onboarding.onboardings,
            preSelectedSolarProject: state.onboarding.preSelectedSolarProject,
            savedSolarProjects: state.onboarding.savedSolarProjects,
        },
        questionnaire: {
            oilGasInfo: state.questionnaire.oilGasInfo,
            riskAnalysisInfo: state.questionnaire.riskAnalysisInfo,
        },
        tax_season: {
            currentTaxSeason: state.tax_season.currentTaxSeason,
            intakeDataById: state.tax_season.intakeDataById,
        },
        gift_tax: {
            currentGiftTax: state.gift_tax.currentGiftTax,
            giftsById: state.gift_tax.giftsById,
        },
    }),
});

// Dedupe concurrent token refreshes (rotating refresh tokens would otherwise
// invalidate each other and bounce the user to login).
let refreshJWTInFlight = null;

// Resolves once the first refreshJWT attempt settles (logged in or not).
// Router guards that classify the visitor (e.g. the solar ToS consent guard)
// must await this: vue-router starts the initial navigation at app.use(router),
// BEFORE main.js's refreshJWT rehydrates the session, so isLoggedIn/user read
// during that first navigation are otherwise still empty on a hard page load.
let resolveSessionRehydrated;
export const sessionRehydrated = new Promise(resolve => {
    resolveSessionRehydrated = resolve;
});

const store = createStore({
    strict: DEBUG,
    plugins: [vuexLocalStorage.plugin],
    modules: {
        account,
        admin,
        ai,
        configuration,
        content,
        error,
        guided_planning,
        hamilton,
        marketing,
        media,
        model,
        notifications,
        notifier,
        onboarding,
        questionnaire,
        reports,
        scenarios,
        subscriptions,
        tasks,
        tax_season,
        gift_tax,
        transition_animation,
        ui,
        uploads,
    },
    state: {
        auth: {},
        impersonatedUser: null,
        filters: {},
        extraHeaders: {},
        loading: false,
        loadingText: '',
        // One-shot handoff of an email already typed on one auth page to the next
        // one that asks for it again (Login -> Forgot password). Deliberately not
        // a route query: `navigateTo` forwards target URLs to PostHog, and a
        // query param would also put the address in browser history and access
        // logs. Not in the localStorage reducer above either — it lives for one
        // navigation and the consumer clears it.
        authEmailPrefill: '',
    },
    getters: {
        isAdvisor: state => {
            return (state.impersonatedUser || state.auth.user || {}).is_advisor || false;
        },
        // Advisor whose self-registration trial has lapsed: the membership exists
        // and is still manually enabled, but the trial no longer grants access —
        // so is_advisor is already false. Drives the read-only "see what you lost"
        // surfaces: the advisor dashboard stays reachable with its actions and the
        // advisor sidebar links disabled. Flag-gated like every other trial surface.
        isTrialLapsedAdvisor: state => {
            const user = state.impersonatedUser || state.auth.user || {};
            return Boolean(
                isFeatureEnabled(FLAG_ADVISOR_SELF_REGISTRATION) &&
                    !user.is_advisor &&
                    user.advisor_workspace?.is_active &&
                    user.advisor_trial?.state === 'expired',
            );
        },
        isImpersonating: state => {
            return Boolean(state.extraHeaders['X-Impersonate']);
        },
        isLoggedIn: state => {
            // Tokens live in HttpOnly cookies (not readable by JS); a rehydrated user
            // (set on login/refresh, cleared on logout) is the client-side signal.
            return Boolean((state.auth.user || {}).id);
        },
        user: state => {
            return state.impersonatedUser || state.auth.user || {};
        },
        axiosConfig: state => {
            const headers = {
                'X-Requested-With': 'XMLHttpRequest',
                ...state.extraHeaders,
            };
            return {
                headers,
                // Send the HttpOnly auth cookies. Scoped here (not a global axios
                // default) so cross-origin calc microservice calls — which need no
                // auth — aren't forced into credentialed CORS.
                withCredentials: true,
                transformResponse(resp) {
                    let data = resp;
                    if (typeof resp === 'string') {
                        try {
                            data = JSON.parse(resp);
                        } catch (e) {
                            /* Ignore */
                        }
                    }
                    if ((data.error || '').indexOf('password expired') !== -1 && data.url) {
                        // Only follow same-origin redirects — never navigate the top frame
                        // to an arbitrary server-supplied URL (open-redirect protection).
                        try {
                            const target = new URL(data.url, window.location.origin);
                            if (target.origin === window.location.origin) {
                                window.location = target.href;
                            }
                        } catch (e) {
                            /* ignore malformed redirect URL */
                        }
                    }
                    return data;
                },
                paramsSerializer: function (params) {
                    return qs.stringify(params, { arrayFormat: 'repeat' });
                },
            };
        },
    },
    mutations: {
        setAuthEmailPrefill(state, email) {
            state.authEmailPrefill = email || '';
        },
        updateImpersonatedUser(state, user) {
            state.impersonatedUser = user;
        },
        updateAuth(state, jwt) {
            const { user = {} } = jwt;
            state.auth = { ...state.auth, ...jwt };

            try {
                if (PRODUCTION && window.Intercom) {
                    window.Intercom('update', {
                        user_id: user.id,
                        name: `${user.first_name} ${user.last_name}`,
                        email: user.email,
                    });
                }
            } catch (e) {
                logError(e);
            }
            // PostHog identify - separate try-catch to ensure silent failure.
            // Self-gated on PostHog being initialized (plugins/posthog.js), so
            // staging gets flag-targeting identities too; no-ops before the
            // boot-chain init and in local dev.
            try {
                if (user.id) {
                    identifyUser(user.id, {
                        email: user.email,
                        is_advisor: user.is_advisor,
                        first_name: user.first_name,
                        last_name: user.last_name,
                        is_staff: user.is_staff,
                        advisor_role: user.advisor_role,
                    });
                }
            } catch (e) {
                // Silent failure - tracking should never break user flow
            }
        },
        updateFilters(state, filters) {
            state.filters = { ...filters };
        },
        updateExtraHeaders(state, headers) {
            state.extraHeaders = { ...headers };
        },
        updateLoading(state, val) {
            state.loading = val;
        },
        updateLoadingText(state, text) {
            state.loadingText = text;
        },
    },
    actions: {
        async socialAuth({ commit, dispatch }, { provider, access_token, extra_data }) {
            try {
                const { data } = await axios.post(
                    `${API_ROOT}/token/social/`,
                    {
                        provider,
                        access_token,
                        extra_data,
                        // Same bucket the flag bootstrap used, so the backend's
                        // authentication-google-login check agrees with the button's visibility.
                        distinct_id: persistedDistinctId(),
                    },
                    { withCredentials: true },
                );
                if (data && data.two_factor_required) {
                    // Existing account with 2FA enabled — run the challenge before
                    // any session is established (see verify2fa).
                    return data;
                }
                const { created, ...auth } = data;
                commit('updateAuth', auth);
                // Parity with `auth`: the token response's user payload can lag
                // the richer /accounts/{uuid}/ serializer (ENG-2629), and the
                // pages branch on onboarding_complete right after this resolves.
                await dispatch('refreshUser');
                dispatch('syncSolarTosConsentOnLogin');
                try {
                    if (created) {
                        trackAccountRegistered({
                            referral_source: extra_data?.where_did_you_hear_about_us || null,
                            method: 'google',
                        });
                    }
                    trackUserLoggedIn({ email: data.user?.email, method: 'google' });
                } catch (e) {
                    // Silent failure - tracking should never break user flow
                }
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async auth({ commit, dispatch }, payload) {
            try {
                const { data } = await axios.post(`${API_ROOT}/token/`, payload, { withCredentials: true });
                // 2FA enabled: the password was correct but there is no session
                // yet. Hand the challenge payload back to the caller (see verify2fa);
                // do NOT commit auth or identify the user until it passes.
                if (data && data.two_factor_required) {
                    return data;
                }
                commit('updateAuth', data);
                // /api/token/ returns only {id} for `user`; fetch the full
                // profile so getters like `isAdvisor`/`isSuper` reflect the
                // server-side flags immediately rather than waiting for some
                // downstream page to call refreshUser. (ENG-2629)
                await dispatch('refreshUser');
                dispatch('syncSolarTosConsentOnLogin');
                try {
                    trackUserLoggedIn({ email: payload.username, method: 'password' });
                } catch (e) {
                    // Silent failure - tracking should never break user flow
                }
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async verify2fa({ commit, dispatch }, payload) {
            // payload: { preauth_token, method, code } or { preauth_token, backup_code }.
            // On success the backend issues the real session cookies + full user.
            // Intentionally no logApiError: a wrong/expired code is an expected
            // challenge outcome, not a session error (which would force a logout).
            const { data } = await axios.post(`${API_ROOT}/token/2fa/verify/`, payload, { withCredentials: true });
            commit('updateAuth', data);
            await dispatch('refreshUser');
            dispatch('syncSolarTosConsentOnLogin');
            try {
                trackUserLoggedIn({ email: data.user?.email, method: payload.login_method || 'password' });
            } catch (e) {
                // Silent failure - tracking should never break user flow
            }
            return data;
        },
        async sendTwoFactorCode(_context, { preauth_token: preauthToken }) {
            // Deliver (or resend) the SMS challenge code for a pending 2FA login.
            const { data } = await axios.post(
                `${API_ROOT}/token/2fa/send/`,
                { preauth_token: preauthToken },
                { withCredentials: true },
            );
            return data;
        },
        async refreshJWT({ commit, dispatch, rootGetters }) {
            // The refresh token lives in an HttpOnly cookie; the backend reads it.
            // Dedupe concurrent refreshes (rotating tokens would otherwise race and
            // bounce the user to login).
            if (refreshJWTInFlight) {
                return refreshJWTInFlight;
            }
            refreshJWTInFlight = (async () => {
                try {
                    const { data } = await axios.post(`${API_ROOT}/token/refresh/`, {}, rootGetters.axiosConfig);
                    // 204 / empty body = no session cookie (anonymous visitor), not an
                    // error — clear state quietly instead of committing empty tokens.
                    if (data && data.access) {
                        commit('updateAuth', data);
                    } else {
                        dispatch('clean_auth');
                    }
                } catch (e) {
                    // No valid refresh cookie → not logged in. Clear state without the
                    // logout side-effects (avoids a redirect / backend-logout loop on load).
                    dispatch('clean_auth');
                } finally {
                    refreshJWTInFlight = null;
                    resolveSessionRehydrated();
                }
            })();
            return refreshJWTInFlight;
        },
        async clean_auth({ commit }) {
            commit('updateAuth', { access: '', refresh: '', user: {} });
        },
        async logout({ commit, dispatch, getters }) {
            if (getters.isLoggedIn && getters.isImpersonating) {
                dispatch('admin/stopImpersonate');
            }
            // Clear the HttpOnly auth cookies server-side. Best effort: this also runs
            // on a 401 (session already gone), so swallow failures to avoid a loop.
            try {
                await axios.post(`${API_ROOT}/registration/logout/`, {}, { withCredentials: true });
            } catch (e) {
                /* already logged out */
            }
            commit('updateAuth', { access: '', refresh: '', user: {} });
            commit('onboarding/updateOnboardings', []);
            commit('hamilton/reset');
            commit('marketing/updateAdvisorProfile', {});
            commit('marketing/updateAdvisoryPartnerProfile', {});
            commit('questionnaire/updateOilGasInfo', {});
            commit('questionnaire/updateRiskAnalysisInfo', {});
            commit('configuration/updateProductsVersion', null);
        },
        async register({ dispatch }, payload) {
            const method = payload.method || 'post';
            payload = {
                username: payload.email,
                ...payload,
            };
            try {
                const { data } = await axios[method](`${API_ROOT}/registration/register/`, payload, {
                    withCredentials: true,
                });
                try {
                    trackAccountRegistered({
                        referral_source: payload.extra_data?.where_did_you_hear_about_us || null,
                        method: 'email',
                    });

                    // Track "Added Client" when registering via advisor's invitation link
                    if (payload.advisor_id) {
                        trackAddedClient({
                            clientEmail: payload.email,
                            advisorEmail: payload.advisor_email || null,
                        });
                    }
                } catch (e) {
                    // Silent failure - tracking should never break user flow
                }

                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async activate({ commit, dispatch }, payload) {
            try {
                const { data } = await axios.get(
                    `${API_ROOT}/registration/activate/${payload.params.uid}/${payload.params.token}/`,
                );
                commit('updateAuth', data);
                try {
                    trackAccountActivated();
                } catch (e) {
                    // Silent failure - tracking should never break user flow
                }
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async sendResendActivation({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${API_ROOT}/registration/resend-activation/`, payload);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async sendResetPassword({ dispatch }, payload) {
            try {
                const { data } = await axios.post(`${API_ROOT}/registration/reset-password/`, payload);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },
        async resetPassword({ dispatch }, payload) {
            try {
                const { data } = await axios.put(`${API_ROOT}/registration/set-password/`, payload);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                throw e;
            }
        },

        // Is this reset link still usable? Asked before rendering the form, so an expired
        // link is reported up front rather than after the user has typed a new password.
        // Resolves to `{ valid, email?, is_first_password? }`. The account and the
        // first-password flag come back only for a valid pair, and let the page say
        // "Set your password" for an invited seat instead of offering to reset one
        // that was never set.
        async validateResetToken(_ctx, payload) {
            const { data } = await axios.post(`${API_ROOT}/registration/set-password/validate/`, payload);
            return data || {};
        },

        async refreshUser({ commit, dispatch, getters, rootGetters }) {
            const user = getters.user;
            // Accounts are addressed by uuid (sequential ids are no longer
            // routable). Sessions persisted before the uuid rollout lack it —
            // force a re-login instead of letting every API call 404.
            const userId = user?.uuid;

            if (!userId) {
                if (user?.id) {
                    dispatch('clean_auth');
                }
                return false;
            }

            try {
                const { data } = await axios.get(`${API_ROOT}/accounts/${userId}/`, rootGetters.axiosConfig);
                if (getters.isImpersonating) {
                    commit('updateImpersonatedUser', data);
                } else {
                    dispatch('saveUser', data);
                }
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        // Where a fresh profile payload lands. Under impersonation that is the
        // impersonated client's record, not the signed-in admin's: every writer
        // addresses the account through `getters.user`, which reads impersonatedUser.
        // Merging into auth.user instead both loses the write — the router's
        // onboarding gate and the whole UI read the getter, so a saved change looked
        // like it never happened — and overwrote the admin's own cached profile with
        // the client's fields. refreshUser has always branched this way; doing it here
        // covers updateUser and the profile.js avatar/email writers too.
        saveUser({ commit, getters, state }, data) {
            if (getters.isImpersonating && state.impersonatedUser) {
                commit('updateImpersonatedUser', { ...state.impersonatedUser, ...data });
                return;
            }
            const user = { ...state.auth.user, ...data };
            const auth = { ...state.auth, user };
            commit('updateAuth', auth);
            commit('marketing/updateMarketingEvents', (user.extra_data || {}).marketing_events);
        },
        async updateUser({ dispatch, getters, rootGetters }, payload) {
            const user = getters.user;
            const userId = user.uuid;
            try {
                const { data } = await axios.patch(`${API_ROOT}/accounts/${userId}/`, payload, rootGetters.axiosConfig);
                dispatch('saveUser', data);
                return data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        /**
         * Dummy action to be used in place of an actual action. Used for testing purposes or to stand in for a real
         * action in v-form steps where we don't actually need to send data to the backend.
         */
        doNothing(_, data) {
            return data;
        },
        async syncSolarTosConsentOnLogin({ dispatch, getters }) {
            const { hasSolarTosConsentInLocalStorage, clearSolarTosConsentFromLocalStorage } = await import(
                '@/utils/solar-tos-consent'
            );
            const user = getters.user;

            if (hasSolarTosConsentInLocalStorage() && user?.id && !user?.solar_marketplace_tos_consent_date) {
                try {
                    await dispatch('updateUser', {
                        ...user,
                        solar_marketplace_tos_consent_date: new Date().toISOString(),
                    });
                    clearSolarTosConsentFromLocalStorage();
                } catch (error) {
                    console.warn('Failed to sync solar ToS consent on login:', error);
                }
            }
        },
    },
});

export default store;
